import {
	Tooltip,
	TooltipContent,
	TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn, formatSegmentDate } from "@/lib/utils";

export interface HistoryDay {
	date: string;
	downtime: number | null;
	availability: number | null;
}

export interface IncidentForSegment {
	startedAt: string;
	endedAt: string | null;
	durationMinutes: number | null;
	message: string | null;
	status: string | null;
}

function formatDuration(minutes: number | null): string {
	if (minutes == null) return "Ongoing";
	if (minutes < 60) return `${minutes} min${minutes !== 1 ? "s" : ""}`;
	const h = Math.floor(minutes / 60);
	const m = minutes % 60;
	if (m === 0) return `${h} hr${h !== 1 ? "s" : ""}`;
	return `${h} hr${h !== 1 ? "s" : ""} ${m} min${m !== 1 ? "s" : ""}`;
}

/** True if date (YYYY-MM-DD) falls within incident's startedAt..endedAt */
function incidentCoversDay(day: string, inc: IncidentForSegment): boolean {
	const dayTime = new Date(`${day}T12:00:00`).getTime();
	const startTime = new Date(
		`${inc.startedAt.slice(0, 10)}T00:00:00`,
	).getTime();
	const endDate = inc.endedAt
		? inc.endedAt.slice(0, 10)
		: new Date().toISOString().slice(0, 10);
	const endTime = new Date(`${endDate}T23:59:59`).getTime();
	return dayTime >= startTime && dayTime <= endTime;
}

interface StatusHistorySegmentsProps {
	history: HistoryDay[];
	/** Incidents for this service; tooltip shows message/status for the day if incident covers it */
	incidents?: IncidentForSegment[];
}

export function StatusHistorySegments({
	history,
	incidents = [],
}: StatusHistorySegmentsProps) {
	// Per-day: incident covering this day (if any)
	const dayIncidentMap = new Map<string, IncidentForSegment>();
	for (const h of history) {
		const inc = incidents.find((i) => incidentCoversDay(h.date, i));
		if (inc) dayIncidentMap.set(h.date, inc);
	}

	/** Color from availability: 1 = green, 0 = red, yellow/orange in between; null = no data (muted) */
	function segmentColor(day: HistoryDay): string {
		const a = day.availability;
		if (a == null) return "bg-muted"; // no data
		if (a >= 1) return "bg-emerald-500";
		if (a <= 0) return "bg-red-500";
		if (a >= 0.99) return "bg-emerald-400";
		if (a >= 0.95) return "bg-lime-400";
		if (a >= 0.9) return "bg-yellow-400";
		if (a >= 0.8) return "bg-amber-500";
		if (a >= 0.7) return "bg-orange-500";
		if (a >= 0.5) return "bg-orange-600";
		return "bg-red-600";
	}

	return (
		<div
			className="gap-0.5 grid grid-cols-90"
			role="img"
			aria-label="Status history last 90 days"
		>
			{history.map((h, i) => {
				const dateStr = formatSegmentDate(h.date);
				const incidentForDay = dayIncidentMap.get(h.date);
				const hasData = h.availability != null;

				return (
					<Tooltip key={`${h.date}-${i}`} delayDuration={0}>
						<TooltipTrigger asChild>
							<div
								className={cn(
									"w-full h-12 rounded-sm shrink-0 cursor-default hover:opacity-90",
									segmentColor(h),
								)}
							/>
						</TooltipTrigger>
						<TooltipContent
							side="top"
							className="text-left whitespace-pre-line p-2 flex flex-col gap-2"
						>
							<p className="font-medium px-2">{dateStr}</p>
							{hasData ? (
								h.downtime != null && h.downtime > 0 ? (
									<span className="grid grid-cols-2 gap-x-4 p-2 bg-muted text-muted-foreground font-medium tabular-nums">
										<p>Downtime</p>
										<p>{formatDuration(h.downtime)}</p>
									</span>
								) : (
									<p className="px-2">No downtime for this day.</p>
								)
							) : (
								<p className="px-2">No data for this day.</p>
							)}
							{incidentForDay?.message && (
								<span className="text-muted-foreground p-4 border-border flex flex-col gap-2">
									<p className="font-medium uppercase">Related</p>
									<pre className="text-muted-foreground">
										{incidentForDay.status && (
											<span
												className={cn(
													"border border-border px-1 rounded-full text-xs uppercase mr-2",
													{
														"border-emerald-500 bg-emerald-500/30 text-emerald-500":
															incidentForDay.status === "resolved",
														"border-destructive bg-destructive/30 text-destructive":
															incidentForDay.status === "investigating",
														"border-muted bg-muted/30 text-muted-foreground":
															incidentForDay.status === "monitoring",
														"border-amber-500 bg-amber-500/30 text-amber-500":
															incidentForDay.status === "identified",
													},
												)}
											>
												{incidentForDay.status}
											</span>
										)}
										{incidentForDay.message}
									</pre>
								</span>
							)}
						</TooltipContent>
					</Tooltip>
				);
			})}
		</div>
	);
}
